'use client';

import border from '@haaretz/l-border.macro';
import color from '@haaretz/l-color.macro';
import config from '@haaretz/l-config';
import fork from '@haaretz/l-fork.macro';
import merge from '@haaretz/l-merge.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import typesetter from '@haaretz/l-type.macro';
import zIndex from '@haaretz/l-z-index.macro';
import useUser from '@haaretz/s-atoms/user';
import Button from '@haaretz/s-button';
import Card from '@haaretz/s-card';
import { site } from '@haaretz/s-consts';
import EmailField from '@haaretz/s-form-fields/EmailField';
import FirstNameField from '@haaretz/s-form-fields/FirstNameField';
import LastNameField from '@haaretz/s-form-fields/LastNameField';
import PhoneField from '@haaretz/s-form-fields/PhoneField';
import HtzLi from '@haaretz/s-htz-li';
import Icon from '@haaretz/s-icon';
import PpModal from '@haaretz/s-pp-modal';
import RadioButton from '@haaretz/s-radio-button';
import RadioGroup from '@haaretz/s-radio-group';
import TextLink from '@haaretz/s-text-link';
import useBi from '@haaretz/s-use-bi';
import React from 'react';
import s9 from 'style9';

import { AstronautImageError, AstronautImageSuccess } from './utils/Astronauts';

import type { InputMaybe } from '@haaretz/s-fragments/Types';
const c = s9.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  innerWrapper: {
    maxWidth: `calc(100vw - ${space(6)})`,
    backgroundColor: color('neutral100'),
    marginRight: 'auto',
    marginLeft: 'auto',
    paddingTop: space(8),
    paddingBottom: space(8),
    paddingRight: space(3),
    paddingLeft: space(3),
    borderRadius: radius('xLarge'),
    marginBottom: fork({ default: space(21), hdc: space(25) }),

    ...merge(
      mq({
        from: 's',
        value: {
          maxWidth: '480px',
        },
      }),
      mq({
        from: 'l',
        value: {
          paddingTop: space(15),
          paddingBottom: space(25),
          paddingRight: space(31),
          paddingLeft: space(31),
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          marginBottom: 0,
          maxWidth: '972px',
        },
      })
    ),
  },
  longerCheckboxTest: {
    marginInlineStart: space(2),
  },
  title: {
    color: color('bgLightAllModes'),
    marginBottom: fork({ default: space(8), hdc: space(10) }),
    marginTop: fork({ default: space(0), hdc: space(8) }),
    paddingRight: space(8),
    paddingLeft: space(8),

    ...typesetter(9),

    ...merge(
      mq({
        from: 'xl',
        until: 'xxl',
        value: {
          maxWidth: fork({ default: '550px', hdc: '600px' }),
          ...typesetter(fork({ default: 11, hdc: 10 })),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          marginTop: fork({ default: space(0), hdc: space(17) }),
          maxWidth: fork({ default: '600px', hdc: '650px' }),
          ...typesetter(fork({ default: 11, hdc: 10 })),
        },
      })
    ),
  },
  subTitle: {
    color: color('neutral100'),
    marginTop: space(3),
    marginBottom: space(3),
    paddingRight: space(3),
    paddingLeft: space(3),
    fontWeight: 400,

    ...typesetter(3),
  },
  fullWidthColumn: {
    height: 'max-content',
    width: '100%',
  },
  detailsWrapper: {
    marginTop: space(8),
    display: 'flex',
    flexDirection: 'column',
    rowGap: fork({ default: space(3), hdc: space(4) }),
    textAlign: 'start',

    ...merge(
      mq({
        from: 'l',
        value: {
          rowGap: space(7),
          marginTop: space(10),
        },
      })
    ),
  },
  innerTitle: {
    ...typesetter(fork({ default: 2, hdc: 1 })),
  },
  innerText: {
    marginBottom: space(8),
    ...typesetter(fork({ default: 1, hdc: 3 })),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(10),
          ...typesetter(fork({ default: 2, hdc: 6 })),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...typesetter(fork({ default: 1, hdc: 5 })),
        },
      })
    ),
  },
  specialOffersWrapper: {
    display: 'flex',
    justifyContent: 'center',
    backgroundImage: `linear-gradient(165.44deg, ${color('primary800')} 9.5%, ${color(
      'primary500'
    )} 81.9%)` as string,
    marginTop: space(-25),
    marginBottom: space(-120),
    paddingTop: space(30),
    zIndex: zIndex('below'),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(-85),
        },
      })
    ),
  },
  subscriptionTypeWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: space(5),

    ...merge(
      mq({
        from: 'l',
        value: {
          flexDirection: 'row',
          columnGap: space(5),
          justifyContent: 'space-between',
        },
      })
    ),
  },
  subscriptionTypeCard: {
    backgroundColor: color('cardGiftSubscription'),
    borderRadius: radius('xLarge'),
    paddingTop: fork({ default: space(5), hdc: space(7) }),
    paddingBottom: fork({ default: space(5), hdc: space(7) }),
    paddingRight: space(3),
    paddingLeft: space(3),
    minWidth: space(57),
    transitionDuration: '0.2s',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease-in-out',

    ':hover': {
      backgroundColor: color('cardGiftSubscriptionHover'),
    },
    ':focus': {
      backgroundColor: color('cardGiftSubscriptionHover'),
    },
    ':active': {
      backgroundColor: color('cardGiftSubscriptionHover'),
    },
  },
  selectedSubscriptionTypeCard: {
    ...border({
      width: '4px',
      color: color('primary800'),
      style: 'solid',
      spacing: fork({ default: 5, hdc: 7 }),
      side: 'block',
    }),
    ...border({
      width: '4px',
      color: color('primary800'),
      style: 'solid',
      spacing: 3,
      side: 'inline',
    }),
  },
  subscriptionTypeText: {
    marginTop: fork({ default: space(0), hdc: space(1) }),
    ...typesetter(2),

    ...merge(
      mq({
        from: 'l',
        value: {
          ...typesetter(0),
        },
      })
    ),
  },
  bold: {
    fontWeight: 700,
  },
  subscriptionTypeTitle: {
    ...typesetter(4),

    ...merge(
      mq({
        from: 'l',
        value: {
          ...typesetter(2),
        },
      })
    ),
  },
  label: {
    ...typesetter(0),

    ...merge(
      mq({
        from: 'l',
        value: {
          ...typesetter(fork({ default: 2, hdc: 0 })),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...typesetter(fork({ default: 1, hdc: -1 })),
        },
      })
    ),
  },
  paymentWrapper: {
    textAlign: 'start',
    marginTop: space(8),
    marginBottom: space(8),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginTop: space(10),
          marginBottom: space(10),
        },
      })
    ),
  },
  paymentTitle: {
    marginBottom: space(3),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(7),
        },
      })
    ),
  },
  ppModal: {
    overflow: 'visible',
    padding: 0,

    ...mq({
      from: 'l',
      value: {
        maxWidth: '540px',
      },
    }),
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: space(28),
    paddingBottom: space(10),
    paddingRight: space(6),
    paddingLeft: space(6),
    textAlign: 'center',

    ...mq({
      from: 'l',
      value: {
        paddingBottom: space(34),
        paddingRight: space(20),
        paddingLeft: space(20),
      },
    }),
  },
  modalContentErrorHdc: {
    ...mq({
      from: 'l',
      value: {
        paddingRight: space(16),
        paddingLeft: space(16),
      },
    }),
  },
  modalTitle: {
    marginBottom: space(3),
    ...typesetter(3),

    ...mq({
      from: 'l',
      value: {
        marginBottom: space(5),
        ...typesetter(2),
      },
    }),
  },
  modalTitleSuccess: {
    marginBottom: space(3),
    ...typesetter(fork({ default: 3, hdc: 2 })),

    ...mq({
      from: 'l',
      value: {
        marginBottom: space(5),
        ...typesetter(4),
      },
    }),
  },
  modalTitleErrorHdc: {
    ...typesetter(1),

    ...merge(mq({ from: 'l', value: { ...typesetter(1) } })),
  },
  modalText: {
    ...typesetter(1),
  },
  modalContentSuccess: {
    paddingTop: space(33),
    paddingBottom: space(12),
    paddingRight: space(fork({ default: 20, hdc: 15 })),
    paddingLeft: space(fork({ default: 20, hdc: 15 })),

    ...mq({
      from: 'l',
      value: {
        paddingBottom: space(27),
        paddingRight: space(fork({ default: 32, hdc: 12 })),
        paddingLeft: space(fork({ default: 32, hdc: 12 })),
      },
    }),
  },
  modalTextSuccess: {
    ...typesetter(fork({ default: 1, hdc: 0 })),

    ...merge(
      mq({
        from: 'l',
        value: {
          ...typesetter(fork({ default: 2, hdc: 1 })),
          maxWidth: '400px',
        },
      })
    ),
  },
  modalTextErrorHdc: {
    ...typesetter(0),

    ...merge(mq({ from: 'l', value: { ...typesetter(1) } })),
  },
  warnIcon: {
    fontSize: space(6),
    alignSelf: 'flex-start',
    verticalAlign: '-0.1em',
    marginInlineEnd: space(1),

    ...mq({
      from: 'l',
      value: {
        fontSize: space(8),
      },
    }),
  },
  list: {
    textAlign: 'start',
    marginTop: fork({ default: space(16), hdc: space(20) }),
    backdropFilter: '-moz-initial',
    ...typesetter(-1, { lines: 6 }),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginTop: space(fork({ default: 25, hdc: 20 })),
        },
      }),
      mq({
        from: 'l',
        until: 'xl',
        value: {
          ...typesetter(0, { lines: 8 }),
        },
      }),
      mq({
        from: 'xl',
        until: 'xxl',
        value: {
          ...typesetter(-1, { lines: 8 }),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...typesetter(fork({ default: -2, hdc: -3 }), { lines: fork({ default: 8, hdc: 5 }) }),
        },
      })
    ),
  },

  giftSubTextHdc: {
    ...typesetter(0),

    ...merge(
      mq({
        from: 'xl',
        until: 'xxl',
        value: {
          ...typesetter(-1),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...typesetter(-2),
        },
      })
    ),
  },
  radioBtnHdc: {
    columnGap: space(3),
    alignItems: 'start',
  },
  radioBtnCircle: {
    marginTop: 'calc((1lh - 1em) / 2)',
  },
});

type UserTypes = 'Giver' | 'Receiver';
type InputTypes = 'firstName' | 'lastName' | 'email' | 'phone';
type NameTypes = `${InputTypes}${UserTypes}`;
type NamesObj = Record<InputTypes, NameTypes>;
type PeriodCalc = 3 | 6 | 12;
type ModalTypes = 'closed' | `success${'Diff' | 'Exist'}Card` | 'error';
type CardTypes = 'existingCard' | 'differentCard';

const inputGiverNames: NamesObj = {
  firstName: 'firstNameGiver',
  lastName: 'lastNameGiver',
  email: 'emailGiver',
  phone: 'phoneGiver',
};

const inputReceiverNames: NamesObj = {
  firstName: 'firstNameReceiver',
  lastName: 'lastNameReceiver',
  email: 'emailReceiver',
  phone: 'phoneReceiver',
};

const textByPeroidCalc = {
  3: fork({ default: 'שלושה חודשים', hdc: '3 Month' }),
  6: fork({ default: 'חצי שנתי', hdc: '6 Month' }),
  12: fork({ default: 'שנתי', hdc: '12 Month' }),
};

const priceByPeriodCalc = {
  3: fork({ default: 147, hdc: 39 }),
  6: fork({ default: 294, hdc: 59 }),
  12: fork({ default: 490, hdc: 79 }),
};

const saleCodeByPeriodCalc = {
  3: 3492,
  6: 3491,
  12: 3488,
};

const cardTypes: Record<CardTypes, CardTypes> = {
  existingCard: 'existingCard',
  differentCard: 'differentCard',
};

const texts: Record<Exclude<ModalTypes, 'closed'>, Record<'title' | 'text', string>> = {
  successExistCard: {
    title: fork({ default: 'תודה!', hdc: 'Thank you!' }),
    text: fork({
      default: 'פרטיך התקבלו. הקבלה תישלח למייל שלך',
      hdc: 'Your details have been received, and a receipt will be sent to your email shortly.',
    }),
  },
  successDiffCard: {
    title: fork({ default: 'תודה!', hdc: `Thank you! We're on it.` }),
    text: fork({
      default: 'אנחנו על זה. שירות הלקוחות יחזור אליך להמשך התהליך',
      hdc: 'Our customer service team will get back to you shortly to continue the process.',
    }),
  },
  error: {
    title: fork({ default: 'אופס, נראה שמשהו השתבש', hdc: 'Oops! Something Went Wrong.' }),
    text: fork({
      default: 'אפשר לנסות שוב, או ליצור קשר עם שירות הלקוחות בטלפון 5200*',
      hdc: 'Please try again, or contact our customer service team at',
    }),
  },
};

function SubscriptionType({
  periodCalc,
  selectedPeriodCalc,
  setSelectedPeriodCalc,
}: {
  periodCalc: PeriodCalc;
  selectedPeriodCalc: PeriodCalc;
  setSelectedPeriodCalc: React.Dispatch<React.SetStateAction<PeriodCalc>>;
}) {
  const currency = fork({ default: '₪', hdc: '$' });

  return (
    <Card
      as="button"
      type="button"
      styleExtend={[
        c.subscriptionTypeCard,
        periodCalc === selectedPeriodCalc && c.selectedSubscriptionTypeCard,
      ]}
      onClick={() => setSelectedPeriodCalc(periodCalc)}
    >
      {fork({
        default: (
          <>
            <p className={s9(c.subscriptionTypeText)}>מסלול</p>
            <h3 className={s9(c.subscriptionTypeTitle)}>{textByPeroidCalc[periodCalc]}</h3>
          </>
        ),
        hdc: (
          <>
            <h3 className={s9(c.subscriptionTypeTitle)}>{textByPeroidCalc[periodCalc]}</h3>
            <p
              className={s9(
                c.subscriptionTypeText,
                ...fork({ default: [], hdc: [c.giftSubTextHdc] })
              )}
            >
              Gift Subscription
            </p>
          </>
        ),
      })}
      <p className={s9(c.subscriptionTypeText, ...fork({ default: [], hdc: [c.bold] }))}>
        {currency}
        {priceByPeriodCalc[periodCalc]}
      </p>
    </Card>
  );
}

function Subscriptions({
  selectedPeriodCalc,
  setSelectedPeriodCalc,
}: {
  selectedPeriodCalc: PeriodCalc;
  setSelectedPeriodCalc: React.Dispatch<React.SetStateAction<PeriodCalc>>;
}) {
  const sharedProps = {
    selectedPeriodCalc,
    setSelectedPeriodCalc,
  };

  const subscriptions = [
    {
      ...sharedProps,
      periodCalc: 3,
    },
    {
      ...sharedProps,
      periodCalc: 6,
    },
    {
      ...sharedProps,
      periodCalc: 12,
    },
  ] as const;

  return (
    <div className={s9(c.subscriptionTypeWrapper)}>
      {subscriptions.map(item => {
        return <SubscriptionType key={item.periodCalc} {...item} />;
      })}
    </div>
  );
}

function GiverForm() {
  const { firstName, lastName, userMail } = useUser('cookieValue');

  return (
    <div className={s9(c.detailsWrapper)}>
      <h2 className={s9(c.innerTitle)}>
        {fork({ default: 'הפרטים שלך', hdc: 'My info (the gift-giver)' })}
      </h2>
      <FirstNameField name={inputGiverNames.firstName} defaultValue={firstName} />
      <LastNameField name={inputGiverNames.lastName} defaultValue={lastName} />
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <EmailField name={inputGiverNames.email} defaultValue={userMail} autoFocus={false} />
      <PhoneField name={inputGiverNames.phone} />
    </div>
  );
}

function ReceiverForm() {
  return (
    <div className={s9(c.detailsWrapper)}>
      <h2 className={s9(c.innerTitle)}>
        {fork({ default: 'פרטי מקבל/ת המתנה', hdc: 'I would like to send a gift subscription to' })}
      </h2>
      <FirstNameField name={inputReceiverNames.firstName} />
      <LastNameField name={inputReceiverNames.lastName} />
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <EmailField name={inputReceiverNames.email} autoFocus={false} />
      <PhoneField name={inputReceiverNames.phone} />
    </div>
  );
}

function PaymentOptions() {
  const sharedProps = {
    styleExtend: fork({ default: [], hdc: [c.radioBtnHdc] }),
    textStyleExtend: [c.label],
    circleStyleExtend: fork({ default: [], hdc: [c.radioBtnCircle] }),
  };

  return (
    <div className={s9(c.paymentWrapper)}>
      <h2 className={s9(c.innerTitle, c.paymentTitle)}>
        {fork({ default: 'איך לשלם על המתנה?', hdc: 'How do you want to pay?' })}
      </h2>
      <RadioGroup defaultValue={cardTypes.existingCard} groupName="card">
        <RadioButton value={cardTypes.existingCard} {...sharedProps}>
          {fork({
            default: 'עם אותו כרטיס אשראי שמשמש לתשלום על המינוי שלי',
            hdc: 'I want to purchase a gift subscription using the credit card I use for my subscription (*If you have chosen to pay via PayPal, Haaretz will be in touch to get your payment details)',
          })}
        </RadioButton>
        <RadioButton value={cardTypes.differentCard} {...sharedProps}>
          {fork({
            default: 'עם כרטיס אשראי אחר',
            hdc: 'I’d like to pay using a different credit card (*Payments can only be made with credit cards)',
          })}
        </RadioButton>
      </RadioGroup>
    </div>
  );
}

function ListItem({ text, isLastItem }: { text: string; isLastItem?: boolean }) {
  return (
    <HtzLi variant="neutral">
      {fork({
        default: isLastItem ? (
          <TextLink href="https://www.haaretz.co.il/misc/terms-of-use">תנאי השימוש</TextLink>
        ) : null,
        hdc: null,
      })}
      {text}
    </HtzLi>
  );
}

const listTexts = [
  fork({
    default: 'המינוי נרכש במתנה עבור חבר/בן משפחה ולא ישמש את הרוכש',
    hdc: 'This subscription has been purchased as a gift and will not be used by the purchaser.',
  }),
  fork({
    default: 'המינוי הוא תקופתי ומוגבל בזמן, בהתאם לתקופה שנרכשה, ואינו מתחדש בתום התקופה',
    hdc: 'This subscription is for a limited period, based on the duration purchased, and will not automatically renew at the end of the period.',
  }),
  fork({
    default: 'מקבל המתנה יקבל התראה מראש לפני תום תקופת המנוי ויוכל לרכוש מינוי לעצמו',
    hdc: 'The gift recipient will receive prior notification before the subscription period ends and will have the option to purchase a subscription for themselves.',
  }),
  fork({
    default: ' של האתר יחולו על רוכש ומקבל המתנה',
    hdc: `The website's terms of use will apply to both the purchaser and the gift recipient.`,
  }),
];

function ResultModal({
  modalType,
  setModalType,
}: {
  modalType: ModalTypes;
  setModalType: React.Dispatch<React.SetStateAction<ModalTypes>>;
}) {
  const isSuccess = modalType.includes('success');
  const isError = modalType === 'error';
  return (
    <PpModal
      styleExtend={[c.ppModal]}
      isOpen={modalType !== 'closed'}
      closeBtnOnClick={() => setModalType('closed')}
      onToggle={isOpen => (isOpen ? undefined : setModalType('closed'))}
      labelledBy={modalType === 'closed' ? null : texts[modalType]?.title}
    >
      {modalType === 'closed' ? null : (
        <div
          className={s9(
            c.modalContent,
            isSuccess && c.modalContentSuccess,
            fork({ default: null, hdc: isError && c.modalContentErrorHdc })
          )}
        >
          {isSuccess ? <AstronautImageSuccess /> : <AstronautImageError />}
          <h2
            className={s9(
              isSuccess ? c.modalTitleSuccess : c.modalTitle,
              fork({ default: null, hdc: isError && c.modalTitleErrorHdc })
            )}
          >
            {isError && <Icon icon="warn" variant="danger" styleExtend={[c.warnIcon]} />}
            {texts[modalType].title}
          </h2>
          <p
            className={s9(
              isSuccess ? c.modalTextSuccess : c.modalText,
              fork({ default: null, hdc: isError && c.modalTextErrorHdc })
            )}
          >
            {texts[modalType].text}
            {fork({
              default: null,
              hdc: isError ? (
                <>
                  <br />
                  <TextLink href="https://www.haaretz.co.il/misc/terms-of-use">
                    digital-english@haaretz.co.il
                  </TextLink>
                </>
              ) : null,
            })}
          </p>
        </div>
      )}
    </PpModal>
  );
}

const msPurchaseUrl = config.get('msPurchaseUrl');

export default function PpGiftSubscriptionPage() {
  const [selectedPeriodCalc, setSelectedPeriodCalc] = React.useState<PeriodCalc>(12);
  const [modalType, setModalType] = React.useState<ModalTypes>('closed');
  const biAction = useBi();
  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const inputs = {
      firstNameGiver: event.currentTarget.elements.namedItem('firstNameGiver'),
      lastNameGiver: event.currentTarget.elements.namedItem('lastNameGiver'),
      emailGiver: event.currentTarget.elements.namedItem('emailGiver'),
      phoneGiver: event.currentTarget.elements.namedItem('phoneGiver'),
      firstNameReceiver: event.currentTarget.elements.namedItem('firstNameReceiver'),
      lastNameReceiver: event.currentTarget.elements.namedItem('lastNameReceiver'),
      emailReceiver: event.currentTarget.elements.namedItem('emailReceiver'),
      phoneReceiver: event.currentTarget.elements.namedItem('phoneReceiver'),
    } as Record<NameTypes, InputMaybe<HTMLInputElement>>;

    const cardOptions = event.currentTarget.elements.namedItem('card') as RadioNodeList;
    let selectedCardValue: CardTypes | undefined;

    if (cardOptions) {
      const seleceted = Array.from(cardOptions).find(option => {
        return (option as HTMLInputElement).checked;
      });

      const selecetedValue = (seleceted as HTMLInputElement).value;

      if (selecetedValue === cardTypes.existingCard || selecetedValue === cardTypes.differentCard) {
        selectedCardValue = selecetedValue;
      }
    }

    if (
      inputs.firstNameGiver &&
      inputs.lastNameGiver &&
      inputs.emailGiver &&
      inputs.phoneGiver &&
      inputs.firstNameReceiver &&
      inputs.lastNameReceiver &&
      inputs.emailReceiver &&
      inputs.phoneReceiver &&
      selectedCardValue
    ) {
      setIsLoading(true);

      const firstNameGiverValue = inputs.firstNameGiver.value.trim();
      const lastNameGiverValue = inputs.lastNameGiver.value.trim();
      const emailGiverValue = inputs.emailGiver.value.trim();
      const phoneGiverValue = inputs.phoneGiver.value.trim();
      const firstNameReceiverValue = inputs.firstNameReceiver.value.trim();
      const lastNameReceiverValue = inputs.lastNameReceiver.value.trim();
      const emailReceiverValue = inputs.emailReceiver.value.trim();
      const phoneReceiverValue = inputs.phoneReceiver.value.trim();

      const formData = {
        gift_type: selectedPeriodCalc.toString(),
        giver_firstname: firstNameGiverValue,
        giver_lastname: lastNameGiverValue,
        giver_mail: emailGiverValue,
        giver_phone: phoneGiverValue,
        reciver_firstname: firstNameReceiverValue,
        reciver_lastname: lastNameReceiverValue,
        reciver_mail: emailReceiverValue,
        reciver_phone: phoneReceiverValue,
        payment_method:
          selectedCardValue === cardTypes.existingCard ? 'existing card' : 'different card',
        site,
      };

      biAction({
        feature: 'gift landing page',
        feature_type: 'Marketing',
        campaign_name: 'gift subscription',
        campaign_details: selectedCardValue === cardTypes.existingCard ? 'my card' : 'other card',
        newsletter_email: emailReceiverValue,
        action_id: 189,
        sale_code: saleCodeByPeriodCalc[selectedPeriodCalc],
        additional_info: formData,
      });

      try {
        const res = await fetch(`${msPurchaseUrl}/helper/gift`, {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify(formData),
        });

        const data = await res.json();

        if (data.status === 'success') {
          setModalType(
            selectedCardValue === cardTypes.existingCard ? 'successExistCard' : 'successDiffCard'
          );
        } else {
          setModalType('error');
        }
      } catch (error) {
        setModalType('error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <div className={s9(c.specialOffersWrapper)} />
      <div className={s9(c.base)}>
        <h1 className={s9(c.title)}>
          {fork({ default: 'זה הזמן לקרוא הארץ', hdc: 'Share the gift of Haaretz' })}
        </h1>
        <div className={s9(c.innerWrapper)}>
          <h3 className={s9(c.innerText)}>
            {fork({
              default:
                'קנו מינוי דיגיטלי להארץ במתנה לבני משפחה או לחברים. עזרו לנו להמשיך בעבודתנו העיתונאית העצמאית והביקורתית.',
              hdc: 'Subscribers can now purchase gift subscriptions at a special discounted rate',
            })}
          </h3>
          <form onSubmit={onSubmit}>
            <Subscriptions
              selectedPeriodCalc={selectedPeriodCalc}
              setSelectedPeriodCalc={setSelectedPeriodCalc}
            />
            <GiverForm />
            <ReceiverForm />
            <PaymentOptions />
            <Button
              variant="sales"
              type="submit"
              {...(isLoading ? { state: 'busy', busyNotice: 'טוען...' } : { state: 'auto' })}
            >
              {fork({ default: 'לרכישה', hdc: 'Buy Now' })}
            </Button>
          </form>
          <ul className={s9(c.list)}>
            {listTexts.map((text, i) => {
              return <ListItem key={text} text={text} isLastItem={i + 1 === listTexts.length} />;
            })}
          </ul>
        </div>
      </div>
      <ResultModal modalType={modalType} setModalType={setModalType} />
    </>
  );
}
